import {
  browserCreateCommunity,
  browserListCommunityCategories,
  CommunityCategory,
  listCommunityCategories,
  ListCommunityCategoriesResponse,
} from "@utils/admin-api";
import { setIsoData, toast } from "@utils/app";
import { isBrowser } from "@utils/browser";
import { validActorRegexPattern } from "@utils/config";
import { getHttpBaseInternal } from "@utils/env";
import { IRoutePropsWithFetch } from "@utils/routes";
import { InitialFetchRequest, RouteDataResponse } from "@utils/types";
import { Component, FormEvent, linkEvent } from "inferno";
import { Link, Prompt, RouteComponentProps, RouterContext } from "inferno-router";
import {
  CommunityResponse,
  CommunityVisibility,
  MyUserInfo,
} from "lemmy-js-client";
import { NoOptionI18nKeys } from "i18next";
import { FirstLoadService } from "@services/FirstLoadService";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
} from "@services/HttpService";
import { I18NextService } from "../../services";
import { HtmlTags } from "../common/html-tags";
import { Icon, Spinner } from "../common/icon";
import { simpleScrollMixin } from "../mixins/scroll-mixin";
import { removeLocalStorageMarkdown } from "@components/common/markdown-textarea";
import { aimooDefaultRules, aimooT } from "@utils/aimoo-i18n";

const TITLE_MAX = 30;
const SUMMARY_MAX = 200;

type CreateCommunityData = RouteDataResponse<{
  listCategoriesResponse: ListCommunityCategoriesResponse;
}>;

interface CreateCommunityState {
  title: string;
  name: string;
  nameTouched: boolean;
  summary: string;
  sidebar: string;
  categories: CommunityCategory[];
  categoryId?: number;
  categoriesLoading: boolean;
  visibility: CommunityVisibility;
  iconPreviewUrl?: string;
  iconFile?: File;
  createCommunityRes: RequestState<CommunityResponse>;
  submitted: boolean;
  isIsomorphic: boolean;
}

export type CreateCommunityFetchConfig = IRoutePropsWithFetch<
  CreateCommunityData,
  Record<string, never>,
  Record<string, never>
>;

@simpleScrollMixin
export class CreateCommunity extends Component<
  RouteComponentProps<Record<string, never>>,
  CreateCommunityState
> {
  private isoData = setIsoData<CreateCommunityData>(this.context);
  private iconInputId = "aimoo-create-community-icon";

  state: CreateCommunityState = {
    title: "",
    name: "",
    nameTouched: false,
    summary: "",
    sidebar: aimooDefaultRules(),
    categories: [],
    categoriesLoading: true,
    visibility: "public",
    createCommunityRes: EMPTY_REQUEST,
    submitted: false,
    isIsomorphic: false,
  };

  constructor(
    props: RouteComponentProps<Record<string, never>>,
    context: object,
  ) {
    super(props, context);

    if (FirstLoadService.isFirstLoad) {
      const listRes = this.isoData.routeData.listCategoriesResponse;
      if (listRes.state === "success") {
        const categories = listRes.data.categories;
        this.state = {
          ...this.state,
          categories,
          categoryId: categories[0]?.id,
          categoriesLoading: false,
          isIsomorphic: true,
        };
      } else {
        this.state = {
          ...this.state,
          categoriesLoading: false,
          isIsomorphic: true,
        };
      }
    }
  }

  async componentDidMount() {
    if (!isBrowser() || this.state.isIsomorphic) {
      return;
    }
    this.setState({ categoriesLoading: true });
    const res = await browserListCommunityCategories();
    if (res.state === "success") {
      this.setState({
        categories: res.data.categories,
        categoryId: this.state.categoryId ?? res.data.categories[0]?.id,
        categoriesLoading: false,
      });
    } else {
      this.setState({ categoriesLoading: false });
      toast(res.err.message || aimooT("load_categories_failed"), "danger");
    }
  }

  componentWillUnmount() {
    revokeIconPreview(this.state.iconPreviewUrl);
  }

  static async fetchInitialData({
    headers,
  }: InitialFetchRequest): Promise<CreateCommunityData> {
    const listCategoriesResponse = await listCommunityCategories(
      getHttpBaseInternal(),
      headers,
    );
    return { listCategoriesResponse };
  }

  get documentTitle(): string {
    return `${I18NextService.i18n.t("create_community")} - ${
      this.isoData.siteRes?.site_view.site.name
    }`;
  }

  render() {
    const {
      title,
      name,
      summary,
      sidebar,
      categories,
      categoryId,
      categoriesLoading,
      visibility,
      iconPreviewUrl,
      createCommunityRes,
      submitted,
    } = this.state;
    const loading = createCommunityRes.state === "loading";
    const previewTitle = title.trim() || aimooT("preview_title_fallback");
    const previewSummary =
      summary.trim() || aimooT("preview_summary_fallback");
    const previewInitial = previewTitle.slice(0, 1);
    const selectedCategory = categories.find(c => c.id === categoryId);

    return (
      <div className="aimoo-create-community">
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />

        <div className="aimoo-create-community__inner container-lg px-3">
          <nav className="aimoo-create-community__breadcrumb" aria-label="breadcrumb">
            <Link to="/" className="aimoo-create-community__breadcrumb-link">
              {aimooT("community")}
            </Link>
            <span className="aimoo-create-community__breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="aimoo-create-community__breadcrumb-current">
              {aimooT("create_community_short")}
            </span>
          </nav>

          <div className="aimoo-create-community__layout">
            <div className="aimoo-create-community__main">
              <form
                className="aimoo-create-community__card"
                onSubmit={linkEvent(this, handleSubmit)}
              >
                <Prompt
                  message={I18NextService.i18n.t("block_leaving")}
                  when={
                    !loading &&
                    !submitted &&
                    !!(title || name || summary)
                  }
                />

                <h1 className="aimoo-create-community__title">
                  {aimooT("create_community_title")}
                </h1>
                <p className="aimoo-create-community__subtitle">
                  {aimooT("create_community_subtitle")}
                </p>

                <div className="aimoo-create-community__field">
                  <label
                    className="aimoo-create-community__label"
                    htmlFor="aimoo-cc-title"
                  >
                    {aimooT("community_name_label")}{" "}
                    <span className="aimoo-create-community__required">*</span>
                  </label>
                  <input
                    id="aimoo-cc-title"
                    type="text"
                    className="form-control aimoo-create-community__input"
                    placeholder={aimooT("community_name_placeholder")}
                    maxLength={TITLE_MAX}
                    required
                    value={title}
                    onInput={linkEvent(this, handleTitleChange)}
                  />
                  <p className="aimoo-create-community__hint">
                    {aimooT("max_chars", { count: TITLE_MAX })}
                  </p>
                </div>

                <div className="aimoo-create-community__field">
                  <label
                    className="aimoo-create-community__label"
                    htmlFor="aimoo-cc-name"
                  >
                    {aimooT("community_slug_label")}{" "}
                    <span className="aimoo-create-community__required">*</span>
                  </label>
                  <input
                    id="aimoo-cc-name"
                    type="text"
                    className="form-control aimoo-create-community__input"
                    placeholder={aimooT("community_slug_placeholder")}
                    required
                    minLength={2}
                    pattern={validActorRegexPattern}
                    title={I18NextService.i18n.t("community_reqs")}
                    value={name}
                    onInput={linkEvent(this, handleNameChange)}
                  />
                  <p className="aimoo-create-community__hint">
                    {aimooT("community_slug_hint")}
                  </p>
                </div>

                <div className="aimoo-create-community__field">
                  <label
                    className="aimoo-create-community__label"
                    htmlFor="aimoo-cc-summary"
                  >
                    {aimooT("community_summary_label")}{" "}
                    <span className="aimoo-create-community__required">*</span>
                  </label>
                  <textarea
                    id="aimoo-cc-summary"
                    className="form-control aimoo-create-community__textarea"
                    rows={3}
                    placeholder={aimooT("community_summary_placeholder")}
                    maxLength={SUMMARY_MAX}
                    required
                    value={summary}
                    onInput={linkEvent(this, handleSummaryChange)}
                  />
                  <p className="aimoo-create-community__hint">
                    {aimooT("max_chars", { count: SUMMARY_MAX })}
                  </p>
                </div>

                <div className="aimoo-create-community__field">
                  <span className="aimoo-create-community__label">
                    {aimooT("community_icon_label")}
                  </span>
                  <div className="aimoo-create-community__icon-row">
                    <label
                      className="aimoo-create-community__icon-upload"
                      htmlFor={this.iconInputId}
                    >
                      {iconPreviewUrl ? (
                        <img
                          src={iconPreviewUrl}
                          alt=""
                          className="aimoo-create-community__icon-preview"
                        />
                      ) : (
                        <span className="aimoo-create-community__icon-placeholder">
                          <Icon icon="add" classes="icon-inline" />
                          <span>{aimooT("upload_icon")}</span>
                        </span>
                      )}
                    </label>
                    <input
                      id={this.iconInputId}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="d-none"
                      onChange={linkEvent(this, handleIconChange)}
                    />
                    <div>
                      <p className="aimoo-create-community__icon-help">
                        {aimooT("icon_format_hint")}
                      </p>
                      <p className="aimoo-create-community__hint mb-0">
                        {aimooT("icon_size_hint")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="aimoo-create-community__field">
                  <span className="aimoo-create-community__label">
                    {aimooT("category")}{" "}
                    <span className="aimoo-create-community__required">*</span>
                  </span>
                  <div className="aimoo-create-community__categories">
                    {categoriesLoading ? (
                      <span className="aimoo-create-community__hint">
                        {aimooT("loading_categories")}
                      </span>
                    ) : categories.length === 0 ? (
                      <span className="aimoo-create-community__hint">
                        {aimooT("no_categories_available")}
                      </span>
                    ) : (
                      categories.map(cat => (
                        <label
                          key={cat.id}
                          className={`aimoo-create-community__category${
                            categoryId === cat.id
                              ? " aimoo-create-community__category--active"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="aimoo-cc-category"
                            className="aimoo-create-community__radio"
                            checked={categoryId === cat.id}
                            onChange={() =>
                              this.setState({ categoryId: cat.id })
                            }
                          />
                          <span>{cat.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="aimoo-create-community__field">
                  <label
                    className="aimoo-create-community__label"
                    htmlFor="aimoo-cc-rules"
                  >
                    {aimooT("community_rules")}
                  </label>
                  <textarea
                    id="aimoo-cc-rules"
                    className="form-control aimoo-create-community__textarea aimoo-create-community__textarea--mono"
                    rows={6}
                    placeholder={aimooT("community_rules_placeholder")}
                    value={sidebar}
                    onInput={linkEvent(this, handleSidebarChange)}
                  />
                  <p className="aimoo-create-community__hint">
                    {aimooT("community_rules_hint")}
                  </p>
                </div>

                <div className="aimoo-create-community__field aimoo-create-community__field--visibility">
                  <span className="aimoo-create-community__label">
                    {aimooT("community_visibility")}
                  </span>
                  <div className="aimoo-create-community__visibility">
                    <label
                      className={`aimoo-create-community__visibility-option${
                        visibility === "public"
                          ? " aimoo-create-community__visibility-option--active"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="aimoo-cc-visibility"
                        className="aimoo-create-community__radio"
                        checked={visibility === "public"}
                        onChange={() => this.setState({ visibility: "public" })}
                      />
                      <span>
                        <span className="aimoo-create-community__visibility-title">
                          {aimooT("visibility_public")}
                        </span>
                        <span className="aimoo-create-community__visibility-desc">
                          {aimooT("visibility_public_desc")}
                        </span>
                      </span>
                    </label>
                    <label
                      className={`aimoo-create-community__visibility-option${
                        visibility === "private"
                          ? " aimoo-create-community__visibility-option--active"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="aimoo-cc-visibility"
                        className="aimoo-create-community__radio"
                        checked={visibility === "private"}
                        onChange={() => this.setState({ visibility: "private" })}
                      />
                      <span>
                        <span className="aimoo-create-community__visibility-title">
                          {aimooT("visibility_restricted")}
                        </span>
                        <span className="aimoo-create-community__visibility-desc">
                          {aimooT("visibility_restricted_desc")}
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="aimoo-create-community__actions">
                  <button
                    type="submit"
                    className="btn btn-primary aimoo-create-community__submit"
                    disabled={loading}
                  >
                    {loading ? <Spinner /> : aimooT("create_community_short")}
                  </button>
                  <Link to="/" className="btn aimoo-create-community__cancel">
                    {aimooT("cancel")}
                  </Link>
                </div>
              </form>
            </div>

            <aside className="aimoo-create-community__preview">
              <div className="aimoo-create-community__preview-sticky">
                <h2 className="aimoo-create-community__preview-heading">
                  <Icon icon="eye" classes="icon-inline me-1" />
                  {aimooT("live_preview")}
                </h2>
                <div className="aimoo-create-community__preview-card">
                  <div className="aimoo-create-community__preview-banner" />
                  <div className="aimoo-create-community__preview-body">
                    {iconPreviewUrl ? (
                      <img
                        src={iconPreviewUrl}
                        alt=""
                        className="aimoo-create-community__preview-avatar"
                      />
                    ) : (
                      <div className="aimoo-create-community__preview-avatar aimoo-create-community__preview-avatar--fallback">
                        {previewInitial}
                      </div>
                    )}
                    <h3 className="aimoo-create-community__preview-title">
                      {previewTitle}
                    </h3>
                    <p className="aimoo-create-community__preview-summary">
                      {previewSummary}
                    </p>
                    <div className="aimoo-create-community__preview-meta">
                      <span>
                        <Icon icon="groups" classes="icon-inline me-1" />
                        {aimooT("one_member")}
                      </span>
                      <span>
                        {selectedCategory?.name ?? aimooT("no_category_selected")}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary w-100 aimoo-create-community__preview-join"
                      disabled
                    >
                      {aimooT("join")}
                    </button>
                  </div>
                </div>
                <p className="aimoo-create-community__preview-note">
                  {aimooT("preview_hint")}
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }
}

function revokeIconPreview(url?: string) {
  if (url && isBrowser()) {
    URL.revokeObjectURL(url);
  }
}

function slugifyName(title: string): string {
  const ascii = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 20);
  if (ascii.length >= 2) {
    return ascii;
  }
  return `c_${Date.now().toString(36)}`;
}

function handleTitleChange(
  i: CreateCommunity,
  event: FormEvent<HTMLInputElement>,
) {
  const title = event.currentTarget.value;
  i.setState(s => ({
    title,
    name: s.nameTouched ? s.name : slugifyName(title),
  }));
}

function handleNameChange(
  i: CreateCommunity,
  event: FormEvent<HTMLInputElement>,
) {
  i.setState({
    name: event.currentTarget.value,
    nameTouched: true,
  });
}

function handleSummaryChange(
  i: CreateCommunity,
  event: FormEvent<HTMLTextAreaElement>,
) {
  i.setState({ summary: event.currentTarget.value });
}

function handleSidebarChange(
  i: CreateCommunity,
  event: FormEvent<HTMLTextAreaElement>,
) {
  i.setState({ sidebar: event.currentTarget.value });
}

function handleIconChange(
  i: CreateCommunity,
  event: FormEvent<HTMLInputElement>,
) {
  const file = event.currentTarget.files?.[0];
  if (!file) {
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    toast(aimooT("icon_too_large"), "danger");
    event.currentTarget.value = "";
    return;
  }
  revokeIconPreview(i.state.iconPreviewUrl);
  i.setState({
    iconFile: file,
    iconPreviewUrl: URL.createObjectURL(file),
  });
}

async function handleSubmit(
  i: CreateCommunity,
  event: FormEvent<HTMLFormElement>,
) {
  event.preventDefault();
  const {
    title,
    name,
    summary,
    sidebar,
    categories,
    categoryId,
    visibility,
    iconFile,
  } = i.state;

  if (!name.trim() || !title.trim() || !summary.trim()) {
    return;
  }
  if (!categoryId) {
    toast(aimooT("select_category_required"), "danger");
    return;
  }

  const selectedCategory = categories.find(c => c.id === categoryId);
  const sidebarWithCategory = [
    `${aimooT("category_md_prefix")}${selectedCategory?.name ?? categoryId}`,
    "",
    sidebar.trim(),
  ]
    .join("\n")
    .trim();

  const form = {
    name: name.trim(),
    title: title.trim(),
    summary: summary.trim(),
    sidebar: sidebarWithCategory,
    visibility,
    category_id: categoryId,
  };

  i.setState({ createCommunityRes: LOADING_REQUEST, submitted: true });
  const res = await browserCreateCommunity(form);
  i.setState({ createCommunityRes: res });

  if (res.state === "success") {
    const myUserInfo = i.isoData.myUserInfo as MyUserInfo | undefined;
    if (myUserInfo) {
      myUserInfo.moderates.push({
        community: res.data.community_view.community,
        moderator: myUserInfo.local_user_view.person,
      });
    }

    if (iconFile) {
      const iconRes = await HttpService.client.uploadCommunityIcon(
        { id: res.data.community_view.community.id },
        { image: iconFile },
      );
      if (iconRes.state === "failed") {
        toast(
          I18NextService.i18n.t(iconRes.err.name as NoOptionI18nKeys),
          "danger",
        );
      }
    }

    removeLocalStorageMarkdown();
    i.props.history.replace(`/c/${res.data.community_view.community.name}`);
  } else if (res.state === "failed") {
    i.setState({ submitted: false });
    const errName = res.err.message;
    toast(
      I18NextService.i18n.exists(errName as NoOptionI18nKeys)
        ? I18NextService.i18n.t(errName as NoOptionI18nKeys)
        : errName || aimooT("create_failed"),
      "danger",
    );
  }
}
